import type { QUERY_PARAMS } from "@/data/constants";
import type { ParsedDomain } from "psl";
import { parse } from "psl";

/**
 * Removes specified query parameters from the current URL without reloading the page.
 *
 * This function takes an array of query parameter names and removes them from the current
 * URL's query string. It updates the browser's URL using the `history.replaceState` method
 * so that the browser's history is not affected (no new entry is added).
 *
 * @param params - An array of strings representing the query parameters to be removed. Defaults to an empty array.
 *
 * @remarks
 * - This function does not trigger a page reload.
 * - It safely handles scenarios where `window.location.href` may be undefined, e.g., in server-side rendering environments.
 *
 * @example
 * ```ts
 * cleanUrlQueryParams(['filter', 'sort']);
 * ```
 */

export function cleanUrlQueryParams(queryParams: QUERY_PARAMS[] = []) {
  const cleanedUrl = new URL(window.location?.href);
  queryParams.forEach(param => cleanedUrl.searchParams.delete(param));
  window.history.replaceState("", "", cleanedUrl);
}

/**
 * Gets the apex (root) domain from a hostname.
 * @param hostname - optional hostname to parse, defaults to current hostname
 * @returns The apex domain as a string
 */

export function getApexDomain(hostname?: URL["hostname"]) {
  hostname = hostname || window.location.hostname;
  const parsed = parse(hostname) as ParsedDomain;
  return parsed?.domain || hostname;
}

/**
 * Checks if a string is a valid URL.
 * @param str - The string to be validated as a URL.
 * @returns True if the string is a valid URL, false otherwise.
 */

export function isValidUrl(str: any) {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}
